import React, { useState } from "react";
import {
  ChevronRight,
  Play,
  Users,
  CheckCircle,
  Eye,
  RotateCw,
  MessageSquare,
  Target,
} from "lucide-react";

export const PodLifecycle = () => {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    // PHASE 1: FORMATION (3-5 days)
    {
      id: "mission",
      title: "Mission Definition",
      subtitle: "Clear North Star",
      icon: Target,
      color: "from-green-500 to-green-600",
      description:
        "Define clear mission with measurable outcomes aligned to client success",
      keyActivities: [
        "Client objectives analysis and alignment",
        "Measurable success criteria definition",
        "Timeline and scope boundaries establishment",
        "Risk assessment and mitigation planning",
      ],
      duration: "1-2 days",
      deliverables: [
        "Mission statement",
        "Success criteria",
        "Risk assessment document",
      ],
      phase: "Formation",
    },
    {
      id: "roles",
      title: "Pod Role Assignment",
      subtitle: "Right People, Right Roles",
      icon: Users,
      color: "from-green-500 to-green-600",
      description:
        "Assign team members to roles based on client needs and project requirements",
      keyActivities: [
        "Skills assessment and role matching",
        "Cross-functional team composition",
        "Backup and knowledge sharing setup",
        "Leadership and accountability structure",
      ],
      duration: "1 day",
      deliverables: ["Team roster", "Role assignments", "RACI matrix"],
      phase: "Formation",
    },
    {
      id: "onboarding",
      title: "Onboarding & Alignment",
      subtitle: "Foundation for Success",
      icon: CheckCircle,
      color: "from-green-600 to-green-700",
      description:
        "Team members are introduced, trained, and aligned with tools, mission, and client integration",
      keyActivities: [
        "Role Orientation: Clear understanding of Pod responsibilities and success metrics",
        "Tools Setup: KolaHub, Slack, Notion, and client-specific platforms",
        "Mission Alignment Workshop: North Star definition and client integration strategy",
        "Client Introduction: Direct stakeholder meetings and expectation setting",
      ],
      duration: "1-2 days",
      deliverables: [
        "Team competency assessment",
        "Mission alignment document",
        "Client integration plan",
      ],
      phase: "Formation",
    },
    // PHASE 2: SPRINT OPERATIONS (2-week cycles)
    {
      id: "assignment",
      title: "Task Assignment",
      subtitle: "Clear Ownership & Accountability",
      icon: CheckCircle,
      color: "from-orange-500 to-red-500",
      description:
        "Roles and tasks are assigned with clear accountability and realistic timelines",
      keyActivities: [
        "Sprint Planning Sessions: Breaking down client objectives into actionable tasks",
        "Role-Based Assignment: Leveraging expertise while ensuring coverage",
        "Dependency Mapping: Identifying task interdependencies and sequencing",
        'Acceptance Criteria Definition: Clear definition of "done" for each deliverable',
      ],
      duration: "1 day per sprint",
      deliverables: [
        "Sprint backlog",
        "Task assignments",
        "Dependency map",
        "Acceptance criteria",
      ],
      phase: "Sprint Operations",
    },
    {
      id: "execution",
      title: "Execution",
      subtitle: "Disciplined Delivery",
      icon: Play,
      color: "from-green-600 to-green-700",
      description:
        "Team members perform assigned tasks with discipline and collaboration",
      keyActivities: [
        "Daily Standups: Progress updates, blocker identification, and mutual support",
        "Collaborative Development: Pair programming, design reviews, knowledge sharing",
        "Client Communication: Regular updates and proactive issue escalation",
        "Cross-functional Integration: Seamless handoffs between roles",
      ],
      duration: "2 weeks per sprint",
      deliverables: [
        "Sprint deliverables",
        "Progress reports",
        "Quality documentation",
      ],
      phase: "Sprint Operations",
    },
    {
      id: "qa",
      title: "Quality Assurance",
      subtitle: "3-Layer Excellence",
      icon: Eye,
      color: "from-green-500 to-green-600",
      description:
        "QA Lead ensures work meets standards before client delivery",
      keyActivities: [
        "Peer Review: Code reviews, design critiques by team members",
        "QA Lead Review: Comprehensive testing against requirements",
        "Pre-Client Validation: Final quality check and readiness assessment",
        "Standards Compliance: Technical and client requirements verification",
      ],
      duration: "Continuous",
      deliverables: ["QA reports", "Test results", "Quality certification"],
      phase: "Sprint Operations",
    },
    {
      id: "monitoring",
      title: "Monitoring",
      subtitle: "Real-time Visibility",
      icon: Eye,
      color: "from-green-500 to-green-600",
      description: "KolaHub tracks progress and flags risks proactively",
      keyActivities: [
        "Real-time Progress Tracking: Task completion and timeline adherence",
        "Risk Flag System: Early warning indicators for potential issues",
        "Performance Metrics: Productivity, quality rates, satisfaction indicators",
        "Resource Utilization: Team capacity and burnout prevention",
      ],
      duration: "Continuous",
      deliverables: [
        "Performance dashboard",
        "Risk assessments",
        "Utilization reports",
      ],
      phase: "Sprint Operations",
    },
    // PHASE 3: CONTINUOUS IMPROVEMENT (ongoing)
    {
      id: "review",
      title: "Review",
      subtitle: "Continuous Improvement",
      icon: RotateCw,
      color: "from-yellow-500 to-orange-500",
      description:
        "Team reviews progress and identifies improvement opportunities",
      keyActivities: [
        "Sprint Retrospectives: What worked, what didn't, actionable improvements",
        "Client Demo & Feedback: Regular showcase with client input integration",
        "Performance Analysis: Quality metrics, timeline adherence review",
        "Team Health Assessment: Cohesion, workload balance, support needs",
      ],
      duration: "1 day per sprint",
      deliverables: [
        "Retrospective notes",
        "Improvement actions",
        "Performance analysis",
      ],
      phase: "Continuous Improvement",
    },
    {
      id: "feedback",
      title: "Feedback Integration",
      subtitle: "Systematic Evolution",
      icon: MessageSquare,
      color: "from-green-600 to-green-700",
      description:
        "Feedback is systematically integrated to improve processes and outcomes",
      keyActivities: [
        "Client Feedback Processing: Rapid integration of client input",
        "Internal Improvement Implementation: Acting on retrospective insights",
        "Cross-Pod Learning: Sharing practices across teams",
        "Process Iteration: Evolving workflows based on experience",
      ],
      duration: "Ongoing",
      deliverables: [
        "Process improvements",
        "Knowledge updates",
        "Best practices",
      ],
      phase: "Continuous Improvement",
    },
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-6 text-brand-primary">
            From Formation to Excellence: The Complete Pod Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our systematic 9-phase approach takes you from Pod formation through
            sprint execution to continuous improvement—ensuring predictable
            delivery, consistent quality, and ongoing evolution.
          </p>
        </div>

        {/* Interactive Timeline */}
        <div className="mb-16">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {phases.map((phase, index) => (
              <button
                key={phase.id}
                onClick={() => setActivePhase(index)}
                className={`flex items-center px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:-translate-y-1 ${
                  activePhase === index
                    ? `bg-gradient-to-r ${phase.color} text-white shadow-lg`
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-brand-secondary shadow-sm"
                }`}
              >
                <phase.icon className="w-5 h-5 mr-2" />
                {phase.title}
              </button>
            ))}
          </div>

          {/* Active Phase Detail */}
          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
            <div
              className={`bg-gradient-to-r ${phases[activePhase].color} p-8 text-white`}
            >
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mr-6">
                  {React.createElement(phases[activePhase].icon, {
                    className: "w-8 h-8",
                  })}
                </div>
                <div>
                  <h3 className="text-3xl font-bold">
                    {phases[activePhase].title}
                  </h3>
                  <p className="text-lg opacity-90">
                    {phases[activePhase].subtitle}
                  </p>
                </div>
              </div>
              <p className="text-lg opacity-90">
                {phases[activePhase].description}
              </p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-bold text-brand-grey mb-6">
                    Key Activities
                  </h4>
                  <ul className="space-y-4">
                    {phases[activePhase].keyActivities.map(
                      (activity, index) => (
                        <li key={index} className="flex items-start">
                          <ChevronRight className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{activity}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div>
                  <div className="bg-brand-secondary border border-gray-200 rounded-2xl p-6">
                    <div className="mb-6">
                      <h5 className="text-lg font-bold text-brand-grey mb-2">
                        Duration
                      </h5>
                      <p className="text-gray-600">
                        {phases[activePhase].duration}
                      </p>
                    </div>

                    <div>
                      <h5 className="text-lg font-bold text-brand-grey mb-4">
                        Key Deliverables
                      </h5>
                      <ul className="space-y-2">
                        {phases[activePhase].deliverables.map(
                          (deliverable, index) => (
                            <li key={index} className="flex items-center">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                              <span className="text-gray-600">
                                {deliverable}
                              </span>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-6">Pod Performance Standards</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="text-3xl font-bold mb-2">≥4.5/5</div>
              <div className="text-green-100">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">≥25%</div>
              <div className="text-green-100">Gross Margin</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">75%</div>
              <div className="text-green-100">Utilization Target</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">2 weeks</div>
              <div className="text-green-100">Sprint Cycles</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
